import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, CheckCircle2, Sparkles, Plus, X, Users, Wrench, Briefcase, CalendarDays, Newspaper, DollarSign, BadgeCheck, Search, Download, Share2 } from "lucide-react";
import CountryIntelAdmin from "@/components/admin/CountryIntelAdmin";
import CitationsManager from "@/components/admin/CitationsManager";
import JobExposureAdmin from "@/components/admin/JobExposureAdmin";
import AIHumanAnalytics from "@/components/admin/AIHumanAnalytics";
import AIProgressAdmin from "@/components/admin/AIProgressAdmin";
import AutoJobsAdmin from "@/components/admin/AutoJobsAdmin";
import DataHubAdmin from "@/components/admin/DataHubAdmin";
import SideHustlesAdmin from "@/components/admin/SideHustlesAdmin";

const field = "w-full bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none";
const COLLS = ["tools", "jobs", "events", "news", "users"];
const EXTRA_TABS = [["jobreqs", "Job Requests"], ["toolreqs", "Tool Requests"], ["eventreqs", "Event Requests"], ["autojobs", "Auto Jobs"], ["messages", "Messages"], ["subscribers", "Subscribers"], ["recruiters", "Recruiter Subs"], ["revenue", "Revenue"], ["countries", "Country Intelligence"], ["citations", "Citations Manager"], ["jobexposure", "AI Job Data"], ["aihuman", "AI Human Views"], ["aiprogress", "AI Progress"], ["datahub", "AI Data Hub"], ["sidehustles", "Side Hustles"]];
const TAB_LABEL = { jobreqs: "job requests", toolreqs: "tool requests", eventreqs: "event requests", messages: "messages", recruiters: "recruiter subscriptions", revenue: "revenue" };
const SUB_URL = { jobreqs: "/admin/submissions/jobs", toolreqs: "/admin/submissions/tools", eventreqs: "/admin/submissions/events", messages: "/admin/submissions/contact" };
const SUB_TABS = ["jobreqs", "toolreqs", "eventreqs", "messages"];

export default function Admin() {
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("tools");
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null); // "gen" | "news" | "tool" | "free"
  const [revenue, setRevenue] = useState(null);
  const [promos, setPromos] = useState([]);
  const [recruiterSubs, setRecruiterSubs] = useState([]);
  const [subscribers, setSubscribers] = useState(null);
  const [busy, setBusy] = useState(false);
  const [userFilter, setUserFilter] = useState("all"); // all | verified | unverified
  const [userSort, setUserSort] = useState("recent"); // recent | verified_date | name
  const [userSearch, setUserSearch] = useState("");
  const [listingStatus, setListingStatus] = useState("all");
  const [listingTier, setListingTier] = useState("all");
  const [dateRange, setDateRange] = useState("all");

  const loadOverview = () => api.get("/admin/overview").then((r) => setOverview(r.data));
  const loadItems = (t) => api.get(SUB_TABS.includes(t) ? SUB_URL[t] : `/admin/${t}`).then((r) => setItems(r.data));

  useEffect(() => { loadOverview(); }, []);
  useEffect(() => {
    setItems([]); setListingStatus("all"); setListingTier("all"); setDateRange("all");
    if (tab === "revenue") {
      api.get("/admin/revenue").then((r) => setRevenue(r.data)).catch(() => {});
      api.get("/admin/promos").then((r) => setPromos(r.data)).catch(() => {});
    }
    else if (tab === "recruiters") {
      api.get("/admin/recruiter-subscriptions").then((r) => setRecruiterSubs(r.data)).catch(() => {});
    }
    else if (tab === "subscribers") {
      api.get("/admin/newsletter-subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
    }
    else if (tab === "countries") { /* handled by CountryIntelAdmin */ }
    else if (tab === "citations") { /* handled by CitationsManager */ }
    else if (tab === "jobexposure") { /* handled by JobExposureAdmin */ }
    else if (tab === "aihuman") { /* handled by AIHumanAnalytics */ }
    else if (tab === "aiprogress") { /* handled by AIProgressAdmin */ }
    else if (tab === "autojobs") { /* handled by AutoJobsAdmin */ }
    else if (tab === "datahub") { /* handled by DataHubAdmin */ }
    else if (tab === "sidehustles") { /* handled by SideHustlesAdmin */ }
    else loadItems(tab);
  }, [tab]);

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    const url = tab === "jobreqs" ? `/admin/submissions/jobs/${id}`
      : tab === "toolreqs" ? `/admin/submissions/tools/${id}`
      : tab === "messages" ? `/admin/submissions/contact/${id}` : `/admin/${tab}/${id}`;
    await api.delete(url);
    toast.success("Deleted"); loadItems(tab); loadOverview();
  };

  const approveJob = async (id) => {
    await api.post(`/admin/submissions/jobs/${id}/approve`);
    toast.success("Approved & published to the jobs board"); loadItems("jobreqs");
  };

  const cancelRecruiterSub = async (subId) => {
    if (!window.confirm("Cancel this recruiter subscription in Stripe?")) return;
    try {
      await api.post(`/admin/recruiter-subscriptions/${subId}/cancel`);
      toast.success("Subscription cancelled");
      api.get("/admin/recruiter-subscriptions").then((r) => setRecruiterSubs(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const approveTool = async (id) => {
    await api.post(`/admin/submissions/tools/${id}/approve`);
    toast.success("Approved & published to the marketplace"); loadItems("toolreqs"); loadOverview();
  };

  const approveEvent = async (id) => {
    await api.post(`/admin/submissions/events/${id}/approve`);
    toast.success("Approved & published to the events directory"); loadItems("eventreqs"); loadOverview();
  };

  const [eventsOverview, setEventsOverview] = useState(null);
  const [eventsRefreshing, setEventsRefreshing] = useState(false);
  const [shareStats, setShareStats] = useState(null);
  useEffect(() => {
    if (tab === "events") api.get("/admin/events-overview").then((r) => setEventsOverview(r.data)).catch(() => {});
    if (tab === "jobs") api.get("/admin/share-stats").then((r) => setShareStats(r.data)).catch(() => {});
  }, [tab]);

  const markRead = async (id) => {
    await api.patch(`/admin/submissions/contact/${id}/read`);
    loadItems("messages");
  };

  const activate = async (id) => {
    await api.patch(`/admin/${tab}/${id}`, { status: "active" });
    toast.success("Activated"); loadItems(tab);
  };

  const toggleVerify = async (it) => {
    await api.patch(`/admin/users/${it.id}/verify`, { verified: !it.verified });
    toast.success(it.verified ? "Verification revoked" : "Company verified");
    loadItems("users");
  };

  const EXPORT_COLS = {
    users: ["company_name", "name", "email", "country", "role", "verified", "verified_source", "verified_at", "created_at"],
    tools: ["name", "tagline", "category", "tier", "status", "company_name", "pricing_model", "website", "created_at"],
    jobs: ["title", "company_name", "location", "country", "remote", "salary", "employment_type", "tier", "status", "apply_url", "created_at"],
    events: ["name", "event_type", "location", "country", "start_date", "end_date", "website", "status", "created_at"],
    news: ["title", "category", "author", "summary", "published_at", "created_at"],
  };

  const exportCsv = (rows, coll) => {
    if (!rows.length) { toast.error("Nothing to export"); return; }
    const cols = EXPORT_COLS[coll] || Object.keys(rows[0]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `worldaiinfo-${coll}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row${rows.length === 1 ? "" : "s"}`);
  };

  const exportRevenueCsv = () => {
    const rows = revenue?.all_transactions || [];
    if (!rows.length) { toast.error("Nothing to export"); return; }
    const cols = ["type", "name", "company", "tier", "amount", "date"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `worldaiinfo-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} transaction${rows.length === 1 ? "" : "s"}`);
  };

  const exportSubscribersCsv = () => {
    const rows = subscribers?.subscribers || [];
    if (!rows.length) { toast.error("Nothing to export"); return; }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = ["email,subscribed_at", ...rows.map((r) => [esc(r.email), esc(r.subscribed_at)].join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `worldaiinfo-newsletter-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} subscriber${rows.length === 1 ? "" : "s"}`);
  };

  const generateNews = async (e) => {
    e.preventDefault(); setBusy(true);
    const f = Object.fromEntries(new FormData(e.target));
    try {
      await api.post("/news/generate", { topic: f.topic, category: f.category });
      toast.success("AI news generated!"); setModal(null);
      if (tab === "news") loadItems("news"); loadOverview();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addNews = async (e) => {
    e.preventDefault(); setBusy(true);
    const f = Object.fromEntries(new FormData(e.target));
    try {
      await api.post("/news", { ...f });
      toast.success("News added!"); setModal(null);
      if (tab === "news") loadItems("news"); loadOverview();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addTool = async (e) => {
    e.preventDefault(); setBusy(true);
    const f = Object.fromEntries(new FormData(e.target));
    const payload = {
      ...f,
      tags: f.tags ? f.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      rating: f.rating ? parseFloat(f.rating) : null,
      featured: f.featured === "on",
      badge: f.badge || null,
    };
    try {
      await api.post("/admin/tools", payload);
      toast.success("Tool added!"); setModal(null);
      if (tab === "tools") loadItems("tools"); loadOverview();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addFreeListing = async (e) => {
    e.preventDefault(); setBusy(true);
    const f = Object.fromEntries(new FormData(e.target));
    const payload = {
      title: f.title, company: f.company, company_website: f.company_website || null,
      location: f.location || null, country: f.country || null,
      salary_from: f.salary_from || null, salary_to: f.salary_to || null,
      currency: f.currency || "USD", job_type: f.job_type || "Full-time",
      is_remote: f.is_remote === "on", description: f.description,
      skills: f.skills || null, apply_url: f.apply_url, contact_email: f.contact_email,
    };
    try {
      await api.post("/admin/free-listing", payload);
      toast.success("Free listing published to the jobs board!"); setModal(null);
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cards = overview ? [
    { label: "Users", value: overview.users, icon: Users },
    { label: "Tools", value: overview.tools, icon: Wrench },
    { label: "Jobs", value: overview.jobs, icon: Briefcase },
    { label: "Events", value: overview.events, icon: CalendarDays },
    { label: "News", value: overview.news, icon: Newspaper },
    { label: "Revenue", value: `$${overview.revenue.toFixed(0)}`, icon: DollarSign },
  ] : [];

  const dateCutoff = React.useMemo(() => {
    const now = new Date();
    if (dateRange === "7d") return new Date(now.getTime() - 7 * 864e5);
    if (dateRange === "30d") return new Date(now.getTime() - 30 * 864e5);
    if (dateRange === "month") return new Date(now.getFullYear(), now.getMonth(), 1);
    if (dateRange === "year") return new Date(now.getFullYear(), 0, 1);
    return null;
  }, [dateRange]);

  const withinRange = (it) => !dateCutoff || (it.created_at && new Date(it.created_at) >= dateCutoff);

  const displayItems = React.useMemo(() => {
    if (tab === "users") {
      const q = userSearch.trim().toLowerCase();
      const list = items.filter((u) => {
        if (userFilter === "verified" && !u.verified) return false;
        if (userFilter === "unverified" && (u.verified || u.role !== "company")) return false;
        if (q && !`${u.name || ""} ${u.company_name || ""} ${u.email || ""}`.toLowerCase().includes(q)) return false;
        return withinRange(u);
      });
      const sorted = [...list];
      if (userSort === "name") sorted.sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""));
      else if (userSort === "verified_date") sorted.sort((a, b) => (b.verified_at || "").localeCompare(a.verified_at || ""));
      else sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      return sorted;
    }
    if (["tools", "jobs", "events"].includes(tab)) {
      return items.filter((it) => {
        if (listingStatus !== "all" && it.status !== listingStatus) return false;
        if (listingTier !== "all" && it.tier !== listingTier) return false;
        return withinRange(it);
      });
    }
    return items.filter(withinRange);
  }, [items, tab, userFilter, userSort, userSearch, listingStatus, listingTier, dateCutoff]);

  const selectCls = "bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm text-[#94A3B8] focus:border-[#00F0FF] focus:outline-none";

  return (
    <div data-testid="admin-page">
      <div className="border-b border-white/10 grid-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <p className="font-mono-tech text-xs uppercase text-[#00F0FF] mb-2">Admin</p>
          <h1 className="font-display font-extrabold text-4xl">Control Center</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-10">
          {cards.map((c) => (
            <div key={c.label} className="card-surface rounded-xl p-4" data-testid={`admin-stat-${c.label.toLowerCase()}`}>
              <c.icon className="w-5 h-5 text-[#00F0FF] mb-3" />
              <div className="font-display font-bold text-2xl">{c.value}</div>
              <div className="font-mono-tech text-xs uppercase text-[#94A3B8]">{c.label}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div className="flex gap-5 border-b border-white/10 flex-wrap">
            {COLLS.map((c) => (
              <button key={c} onClick={() => setTab(c)} data-testid={`admin-tab-${c}`}
                className={`pb-3 -mb-px border-b-2 capitalize transition ${tab === c ? "border-[#00F0FF] text-[#00F0FF]" : "border-transparent text-[#94A3B8]"}`}>
                {c}
              </button>
            ))}
            {EXTRA_TABS.map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
                className={`pb-3 -mb-px border-b-2 transition ${tab === key ? "border-[#00F0FF] text-[#00F0FF]" : "border-transparent text-[#94A3B8]"}`}>
                {label}
                {key === "autojobs" && overview?.pending_jobs > 0 && (
                  <span data-testid="autojobs-tab-badge"
                    className="ml-1.5 inline-flex items-center justify-center text-xs font-semibold px-1.5 py-0.5 rounded-full bg-[#FBBF24] text-[#1a1204]">
                    {overview.pending_jobs}
                  </span>
                )}
              </button>
            ))}
          </div>
          {tab === "news" && (
            <div className="flex gap-2">
              <button onClick={() => setModal("gen")} data-testid="admin-gen-news"
                className="flex items-center gap-2 bg-[#00F0FF] text-[#04121a] font-semibold px-4 py-2 rounded-md hover:brightness-110">
                <Sparkles className="w-4 h-4" /> Generate AI News
              </button>
              <button onClick={() => setModal("news")} data-testid="admin-add-news"
                className="flex items-center gap-2 border border-white/15 px-4 py-2 rounded-md hover:border-[#00F0FF]/60">
                <Plus className="w-4 h-4" /> Add manually
              </button>
              <button onClick={() => exportCsv(displayItems, "news")} data-testid="admin-news-export"
                className="flex items-center gap-2 border border-white/15 px-3 py-2 rounded-md text-sm text-[#94A3B8] hover:border-[#00F0FF]/60 hover:text-[#00F0FF] transition">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>
          )}
          {tab === "users" && (
            <div className="flex gap-2 flex-wrap">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search name or email…" data-testid="admin-users-search"
                  className="bg-[#0B0F19] border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm text-[#F8FAFC] focus:border-[#00F0FF] focus:outline-none" />
              </div>
              <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} className={selectCls} data-testid="admin-users-filter">
                <option value="all">All companies</option>
                <option value="verified">Verified only</option>
                <option value="unverified">Unverified only</option>
              </select>
              <select value={userSort} onChange={(e) => setUserSort(e.target.value)} className={selectCls} data-testid="admin-users-sort">
                <option value="recent">Newest first</option>
                <option value="verified_date">Verified date</option>
                <option value="name">Name (A–Z)</option>
              </select>
              <button onClick={() => exportCsv(displayItems, "users")} data-testid="admin-users-export"
                className="flex items-center gap-2 border border-white/15 px-3 py-2 rounded-md text-sm text-[#94A3B8] hover:border-[#00F0FF]/60 hover:text-[#00F0FF] transition">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>
          )}
          {["tools", "jobs", "events"].includes(tab) && (
            <div className="flex gap-2 flex-wrap">
              {tab === "tools" && (
                <button onClick={() => setModal("tool")} data-testid="admin-add-tool"
                  className="flex items-center gap-2 bg-[#00F0FF] text-[#04121a] font-semibold px-4 py-2 rounded-md hover:brightness-110 text-sm">
                  <Plus className="w-4 h-4" /> Add tool
                </button>
              )}
              <select value={listingStatus} onChange={(e) => setListingStatus(e.target.value)} className={selectCls} data-testid="admin-listing-status">
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                {tab !== "events" && <option value="pending_payment">Pending payment</option>}
              </select>
              {tab === "tools" && (
                <select value={listingTier} onChange={(e) => setListingTier(e.target.value)} className={selectCls} data-testid="admin-listing-tier">
                  <option value="all">All tiers</option>
                  <option value="free">Free</option>
                  <option value="featured">Featured</option>
                  <option value="premium">Premium</option>
                </select>
              )}
              {tab === "jobs" && (
                <select value={listingTier} onChange={(e) => setListingTier(e.target.value)} className={selectCls} data-testid="admin-listing-tier">
                  <option value="all">All tiers</option>
                  <option value="standard">Standard</option>
                  <option value="featured">Featured</option>
                </select>
              )}
              <button onClick={() => exportCsv(displayItems, tab)} data-testid={`admin-${tab}-export`}
                className="flex items-center gap-2 border border-white/15 px-3 py-2 rounded-md text-sm text-[#94A3B8] hover:border-[#00F0FF]/60 hover:text-[#00F0FF] transition">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>
          )}
        </div>

        {tab === "events" && eventsOverview && (
          <div className="mb-6" data-testid="admin-events-overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
              {[["Total Events", eventsOverview.total], ["Active", eventsOverview.active], ["Auto-Discovered", eventsOverview.auto_discovered], ["Manually Seeded", eventsOverview.manually_seeded]].map(([l, v]) => (
                <div key={l} className="card-surface rounded-xl p-4">
                  <div className="font-display font-bold text-2xl text-[#00F0FF]">{v ?? 0}</div>
                  <div className="font-mono-tech text-xs uppercase text-[#94A3B8] mt-1">{l}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div className="flex flex-wrap gap-2" data-testid="events-by-source">
                {Object.entries(eventsOverview.by_source || {}).map(([src, n]) => (
                  <span key={src} className="font-mono-tech text-xs px-3 py-1.5 rounded-full border border-white/12 text-[#94A3B8]">
                    {src === "anchor" ? "Manual (anchor)" : src}: <span className="text-[#00F0FF] font-bold">{n}</span>
                  </span>
                ))}
              </div>
              <button data-testid="events-refresh-btn" disabled={eventsRefreshing}
                onClick={async () => {
                  setEventsRefreshing(true);
                  try {
                    const { data } = await api.post("/admin/events/refresh");
                    toast.success(`Discovered ${data.discovered}, added ${data.added}. ${data.active} active.`);
                    const r = await api.get("/admin/events-overview"); setEventsOverview(r.data);
                  } catch (e) { toast.error("Refresh failed"); }
                  finally { setEventsRefreshing(false); }
                }}
                className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-[#00F0FF] text-[#04121a] font-semibold hover:brightness-110 transition disabled:opacity-60">
                {eventsRefreshing ? "Refreshing…" : "Run auto-discovery now"}
              </button>
            </div>
            <div className="rounded-lg border border-[#F59E0B]/30 bg-[#F59E0B]/[0.06] px-4 py-2.5">
              <p className="text-xs text-[#fbbf24]">{eventsOverview.integration_note}</p>
            </div>
          </div>
        )}

        {tab === "revenue" && revenue && (
          <div data-testid="admin-revenue-dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                ["Total Revenue", `$${revenue.total_revenue.toFixed(2)}`],
                ["Paid Listings", revenue.total_paid_listings],
                ["Paid Jobs", revenue.paid_jobs],
                ["Paid Tools", revenue.paid_tools],
              ].map(([label, val]) => (
                <div key={label} className="card-surface rounded-xl p-5" data-testid={`revenue-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="font-display font-bold text-2xl text-[#00F0FF]">{val}</div>
                  <div className="font-mono-tech text-xs uppercase text-[#94A3B8] mt-1">{label}</div>
                </div>
              ))}
            </div>

            {promos.length > 0 && (
              <div className="mb-8" data-testid="admin-promo-stats">
                <h3 className="font-display font-bold text-lg mb-3">Promo codes</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {promos.map((p) => (
                    <div key={p.code} className="card-surface rounded-xl p-5 border border-[#F59E0B]/30" data-testid={`promo-stat-${p.code}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono font-extrabold text-[#FBBF24]">{p.code}</span>
                        <span className="font-mono-tech text-xs text-[#94A3B8]">{p.discount_pct}% off · expires {(p.expires_at || "").slice(0, 10)}</span>
                      </div>
                      <div className="font-display font-bold text-2xl">
                        {p.used} <span className="text-[#94A3B8] text-lg font-normal">/ {p.max_redemptions} redeemed</span>
                      </div>
                      <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-[#F59E0B]" style={{ width: `${Math.min(100, (p.used / p.max_redemptions) * 100)}%` }} />
                      </div>
                      <div className="font-mono-tech text-xs text-[#94A3B8] mt-2">{p.remaining} remaining</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h3 className="font-display font-bold text-lg mb-3">Revenue by tier</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[["premium", "Premium"], ["featured", "Featured"], ["standard", "Standard/Basic"], ["other", "Other"]].map(([k, label]) => (
                <div key={k} className="card-surface rounded-xl p-4" data-testid={`revenue-tier-${k}`}>
                  <div className="font-display font-bold text-xl">${(revenue.by_tier[k] || 0).toFixed(2)}</div>
                  <div className="font-mono-tech text-xs uppercase text-[#94A3B8] mt-1">{label}</div>
                </div>
              ))}
            </div>

            {revenue.monthly && (
              <>
                <h3 className="font-display font-bold text-lg mb-3">Monthly revenue (last 6 months)</h3>
                <div className="card-surface rounded-xl p-6 mb-8" data-testid="revenue-monthly-chart">
                  {(() => {
                    const max = Math.max(...revenue.monthly.map((m) => m.amount), 1);
                    return (
                      <div className="flex items-end justify-between gap-3 h-44">
                        {revenue.monthly.map((m) => (
                          <div key={m.key} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`revenue-bar-${m.key}`}>
                            <span className="font-mono-tech text-[10px] text-[#94A3B8] mb-1">${m.amount >= 1000 ? `${(m.amount / 1000).toFixed(1)}k` : m.amount.toFixed(0)}</span>
                            <div className="w-full rounded-t-md bg-gradient-to-t from-[#00F0FF]/40 to-[#00F0FF] transition-all"
                              style={{ height: `${Math.max((m.amount / max) * 100, m.amount > 0 ? 4 : 1)}%` }} />
                            <span className="font-mono-tech text-xs text-[#94A3B8] mt-2">{m.month}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </>
            )}

            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg">Recent transactions</h3>
              {revenue.all_transactions && revenue.all_transactions.length > 0 && (
                <button onClick={exportRevenueCsv} data-testid="revenue-export-csv"
                  className="flex items-center gap-2 border border-white/15 px-3 py-1.5 rounded-md text-sm hover:border-[#00F0FF]/60 transition">
                  <Download className="w-4 h-4" /> Export CSV
                </button>
              )}
            </div>
            {revenue.recent.length === 0 ? (
              <div className="text-[#94A3B8] py-8 text-center card-surface rounded-xl">No paid transactions yet.</div>
            ) : (
              <div className="card-surface rounded-xl overflow-hidden">
                <table className="w-full text-sm" data-testid="revenue-recent-table">
                  <thead className="bg-white/5 text-[#94A3B8] font-mono-tech text-xs uppercase">
                    <tr>
                      <th className="text-left px-4 py-3">Type</th>
                      <th className="text-left px-4 py-3">Item</th>
                      <th className="text-left px-4 py-3">Tier</th>
                      <th className="text-right px-4 py-3">Amount</th>
                      <th className="text-right px-4 py-3">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenue.recent.map((r, i) => (
                      <tr key={i} className="border-t border-white/5" data-testid={`revenue-txn-${i}`}>
                        <td className="px-4 py-3"><span className="font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded bg-[#00F0FF]/10 text-[#00F0FF]">{r.type}</span></td>
                        <td className="px-4 py-3 text-white truncate max-w-[220px]">{r.name}{r.company ? <span className="text-[#94A3B8]"> · {r.company}</span> : ""}</td>
                        <td className="px-4 py-3 text-[#94A3B8]">{r.tier || "—"}</td>
                        <td className="px-4 py-3 text-right text-[#4ade80] font-semibold">${(r.amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-right text-[#64748B] font-mono-tech text-xs">{r.date ? new Date(r.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "recruiters" && (
          <div data-testid="admin-recruiter-subs">
            <p className="font-mono-tech text-xs text-[#94A3B8] mb-4">Showing {recruiterSubs.length} recruiter subscription{recruiterSubs.length === 1 ? "" : "s"}</p>
            {recruiterSubs.length === 0 ? (
              <div className="card-surface rounded-xl p-10 text-center text-[#94A3B8]">
                No recruiter subscriptions yet. They appear here automatically after a company subscribes via a Stripe Payment Link.
              </div>
            ) : (
              <div className="card-surface rounded-xl overflow-x-auto">
                <table className="w-full text-sm" data-testid="recruiter-subs-table">
                  <thead>
                    <tr className="text-left text-[#64748B] font-mono-tech text-xs border-b border-white/10">
                      <th className="px-4 py-3">Company</th>
                      <th className="px-4 py-3">Plan</th>
                      <th className="px-4 py-3 text-right">Monthly</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Next billing</th>
                      <th className="px-4 py-3 text-right">Months</th>
                      <th className="px-4 py-3 text-right">Revenue</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recruiterSubs.map((s) => {
                      const badge = { active: "text-[#4ade80] bg-[#22C55E]/10", cancelled: "text-[#94A3B8] bg-white/5", past_due: "text-[#f87171] bg-[#EF4444]/10" }[s.status] || "text-[#94A3B8] bg-white/5";
                      return (
                        <tr key={s.subscription_id} className="border-t border-white/5" data-testid={`recruiter-sub-${s.subscription_id}`}>
                          <td className="px-4 py-3 text-white">{s.company_name || s.email || "—"}</td>
                          <td className="px-4 py-3 text-[#94A3B8]">{s.plan_label || s.plan}</td>
                          <td className="px-4 py-3 text-right text-[#4ade80]">${(s.amount || 0).toFixed(2)}</td>
                          <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-mono-tech capitalize ${badge}`}>{(s.status || "").replace("_", " ")}</span></td>
                          <td className="px-4 py-3 text-[#64748B] font-mono-tech text-xs">{s.next_billing_date ? new Date(s.next_billing_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                          <td className="px-4 py-3 text-right text-[#94A3B8]">{s.months_subscribed || 0}</td>
                          <td className="px-4 py-3 text-right text-[#4ade80] font-semibold">${(s.total_revenue || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">
                            {s.status !== "cancelled" && (
                              <button onClick={() => cancelRecruiterSub(s.subscription_id)} data-testid={`recruiter-cancel-${s.subscription_id}`}
                                className="text-xs text-[#f87171] border border-[#EF4444]/30 px-3 py-1.5 rounded-md hover:bg-[#EF4444]/10 transition">
                                Cancel
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "subscribers" && (
          <div data-testid="admin-subscribers">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <h3 className="font-display font-bold text-lg">Newsletter Subscribers</h3>
                <p className="text-sm text-[#94A3B8]" data-testid="subscribers-count">{subscribers?.total ?? 0} email{(subscribers?.total ?? 0) === 1 ? "" : "s"} captured</p>
              </div>
              {(subscribers?.subscribers || []).length > 0 && (
                <button onClick={exportSubscribersCsv} data-testid="subscribers-export-csv"
                  className="text-sm border border-[#00F0FF]/40 text-[#00F0FF] px-4 py-2 rounded-md hover:bg-[#00F0FF]/10 transition">
                  Export CSV
                </button>
              )}
            </div>
            {(subscribers?.subscribers || []).length === 0 ? (
              <div className="text-[#94A3B8] py-12 text-center">No subscribers yet.</div>
            ) : (
              <div className="card-surface rounded-xl overflow-x-auto">
                <table className="w-full text-sm" data-testid="subscribers-table">
                  <thead>
                    <tr className="text-left text-[#64748B] font-mono-tech text-xs border-b border-white/10">
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Subscribed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(subscribers.subscribers).map((s, i) => (
                      <tr key={i} className="border-t border-white/5" data-testid={`subscriber-row-${i}`}>
                        <td className="px-4 py-3 text-white"><a href={`mailto:${s.email}`} className="hover:text-[#00F0FF]">{s.email}</a></td>
                        <td className="px-4 py-3 text-[#64748B] font-mono-tech text-xs">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab !== "revenue" && tab !== "recruiters" && tab !== "subscribers" && tab !== "autojobs" && tab !== "datahub" && tab !== "sidehustles" && tab !== "countries" && tab !== "citations" && tab !== "jobexposure" && tab !== "aihuman" && tab !== "aiprogress" && (<>
        {tab === "jobs" && shareStats && shareStats.top && shareStats.top.length > 0 && (
          <div className="card-surface rounded-xl p-5 mb-5" data-testid="most-shared-week">
            <h3 className="font-display font-bold text-lg flex items-center gap-2 mb-3"><Share2 className="w-5 h-5 text-[#00F0FF]" /> Most Shared This Week</h3>
            <div className="space-y-2">
              {shareStats.top.map((s, idx) => (
                <div key={s.job_id} className="flex items-center justify-between gap-3 py-1.5 border-b border-white/5 last:border-0" data-testid={`most-shared-${idx}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-display font-bold text-[#64748B] w-5 text-right">{idx + 1}</span>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{s.title}</p>
                      {s.company && <p className="text-xs text-[#64748B] truncate">{s.company}</p>}
                    </div>
                  </div>
                  <span className="shrink-0 font-mono-tech text-xs px-2.5 py-1 rounded-full bg-[#00F0FF]/12 text-[#00F0FF] border border-[#00F0FF]/25">
                    {s.shares} share{s.shares === 1 ? "" : "s"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <p className="font-mono-tech text-xs text-[#94A3B8]" data-testid="admin-count-indicator">
            Showing {displayItems.length} of {items.length} {TAB_LABEL[tab] || tab}
          </p>
          <div className="flex items-center gap-3">
            {tab === "jobreqs" && (
              <button onClick={() => setModal("free")} data-testid="admin-add-free-listing"
                className="flex items-center gap-2 border border-[#22C55E]/40 text-[#4ade80] px-3 py-2 rounded-md text-sm hover:bg-[#22C55E]/10 transition">
                <Plus className="w-4 h-4" /> Add Free Listing
              </button>
            )}
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className={selectCls} data-testid="admin-date-range">
              <option value="all">All time</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="month">This month</option>
              <option value="year">This year</option>
            </select>
          </div>
        </div>
        <div className="space-y-3">
          {displayItems.length === 0 && <div className="text-[#94A3B8] py-12 text-center">No {TAB_LABEL[tab] || tab}.</div>}

          {tab === "messages" && displayItems.map((it) => (
            <div key={it.id} className="card-surface rounded-lg p-4" data-testid={`admin-item-${it.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold">{it.subject || "(no subject)"}</span>
                    {it.status === "new"
                      ? <span className="text-[10px] font-mono-tech uppercase px-2 py-0.5 rounded-full bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/30">New</span>
                      : <span className="text-[10px] font-mono-tech uppercase px-2 py-0.5 rounded-full bg-white/5 text-[#94A3B8] border border-white/10">Read</span>}
                  </div>
                  <p className="text-sm text-[#94A3B8] mt-1">
                    {it.name} · <a href={`mailto:${it.email}`} className="text-[#00F0FF] hover:underline">{it.email}</a>
                    {it.created_at ? ` · ${new Date(it.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
                  </p>
                  <p className="text-sm text-[#cbd5e1] mt-2 whitespace-pre-wrap">{it.message}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {it.status === "new" && (
                    <button onClick={() => markRead(it.id)} data-testid={`admin-mark-read-${it.id}`}
                      className="flex items-center gap-1 text-xs text-[#10B981] border border-[#10B981]/30 px-2.5 py-1.5 rounded hover:bg-[#10B981]/10">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Mark read
                    </button>
                  )}
                  <button onClick={() => del(it.id)} data-testid={`admin-delete-${it.id}`}
                    className="text-[#EF4444] hover:bg-[#EF4444]/10 p-2 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}

          {tab === "jobreqs" && displayItems.map((it) => (
            <div key={it.id} className="card-surface rounded-lg p-4" data-testid={`admin-item-${it.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold">{it.title}</span>
                    {it.paid && (
                      <span className="text-[10px] font-mono-tech uppercase px-2 py-0.5 rounded-full bg-[#22C55E]/20 text-[#4ade80] border border-[#22C55E]/40" data-testid={`admin-paid-badge-${it.id}`}>
                        ✓ Paid{it.amount_paid ? ` $${it.amount_paid}` : ""}
                      </span>
                    )}
                    {it.tier_label && (
                      <span className="text-[10px] font-mono-tech uppercase px-2 py-0.5 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30">{it.tier_label}</span>
                    )}
                    <span className={`text-[10px] font-mono-tech uppercase px-2 py-0.5 rounded-full border ${it.status === "approved" ? "bg-[#22C55E]/15 text-[#4ade80] border-[#22C55E]/30" : "bg-[#F59E0B]/15 text-[#fbbf24] border-[#F59E0B]/30"}`}>
                      {it.status === "approved" ? "Approved" : "Pending Review"}
                    </span>
                  </div>
                  <p className="text-sm text-[#94A3B8] mt-1">
                    {it.company}{it.location ? ` · ${it.location}` : ""}{it.job_type ? ` · ${it.job_type}` : ""}{it.is_remote ? " · Remote" : ""}
                  </p>
                  <p className="text-sm text-[#94A3B8] mt-0.5">
                    Contact: <a href={`mailto:${it.contact_email}`} className="text-[#00F0FF] hover:underline">{it.contact_email}</a>
                    {" · "}<a href={it.apply_url} target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline">Apply URL</a>
                  </p>
                  {it.description && <p className="text-sm text-[#cbd5e1] mt-2 line-clamp-2">{it.description}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {it.status !== "approved" && (
                    <button onClick={() => approveJob(it.id)} data-testid={`admin-approve-${it.id}`}
                      className="flex items-center gap-1 text-xs text-[#10B981] border border-[#10B981]/30 px-2.5 py-1.5 rounded hover:bg-[#10B981]/10">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                    </button>
                  )}
                  <button onClick={() => del(it.id)} data-testid={`admin-delete-${it.id}`}
                    className="text-[#EF4444] hover:bg-[#EF4444]/10 p-2 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}

          {tab === "toolreqs" && displayItems.map((it) => (
            <div key={it.id} className="card-surface rounded-lg p-4" data-testid={`admin-item-${it.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold">{it.name}</span>
                    {it.paid && (
                      <span className="text-[10px] font-mono-tech uppercase px-2 py-0.5 rounded-full bg-[#22C55E]/20 text-[#4ade80] border border-[#22C55E]/40" data-testid={`admin-paid-badge-${it.id}`}>
                        ✓ Paid{it.amount_paid ? ` $${it.amount_paid}` : ""}
                      </span>
                    )}
                    {it.tier_label && (
                      <span className="text-[10px] font-mono-tech uppercase px-2 py-0.5 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30">{it.tier_label}</span>
                    )}
                    <span className={`text-[10px] font-mono-tech uppercase px-2 py-0.5 rounded-full border ${it.status === "approved" ? "bg-[#22C55E]/15 text-[#4ade80] border-[#22C55E]/30" : "bg-[#F59E0B]/15 text-[#fbbf24] border-[#F59E0B]/30"}`}>
                      {it.status === "approved" ? "Approved" : "Pending Review"}
                    </span>
                  </div>
                  <p className="text-sm text-[#94A3B8] mt-1">{it.company}{it.category ? ` · ${it.category}` : ""}{it.pricing_model ? ` · ${it.pricing_model}` : ""}</p>
                  <p className="text-sm text-[#94A3B8] mt-0.5">
                    Contact: <a href={`mailto:${it.contact_email}`} className="text-[#00F0FF] hover:underline">{it.contact_email}</a>
                    {" · "}<a href={it.website} target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline">Website</a>
                  </p>
                  {it.description && <p className="text-sm text-[#cbd5e1] mt-2 line-clamp-2">{it.description}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {it.status !== "approved" && (
                    <button onClick={() => approveTool(it.id)} data-testid={`admin-approve-${it.id}`}
                      className="flex items-center gap-1 text-xs text-[#10B981] border border-[#10B981]/30 px-2.5 py-1.5 rounded hover:bg-[#10B981]/10">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                    </button>
                  )}
                  <button onClick={() => del(it.id)} data-testid={`admin-delete-${it.id}`}
                    className="text-[#EF4444] hover:bg-[#EF4444]/10 p-2 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}

          {tab === "eventreqs" && displayItems.map((it) => (
            <div key={it.id} className="card-surface rounded-lg p-4" data-testid={`admin-item-${it.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold">{it.title}</span>
                    {it.paid && (
                      <span className="text-[10px] font-mono-tech uppercase px-2 py-0.5 rounded-full bg-[#22C55E]/20 text-[#4ade80] border border-[#22C55E]/40" data-testid={`admin-paid-badge-${it.id}`}>
                        ✓ Paid{it.amount_paid ? ` $${it.amount_paid}` : ""}
                      </span>
                    )}
                    {it.tier_label && (
                      <span className="text-[10px] font-mono-tech uppercase px-2 py-0.5 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30">{it.tier_label}</span>
                    )}
                    <span className={`text-[10px] font-mono-tech uppercase px-2 py-0.5 rounded-full border ${it.status === "approved" ? "bg-[#22C55E]/15 text-[#4ade80] border-[#22C55E]/30" : "bg-[#F59E0B]/15 text-[#fbbf24] border-[#F59E0B]/30"}`}>
                      {it.status === "approved" ? "Approved" : "Pending Review"}
                    </span>
                  </div>
                  <p className="text-sm text-[#94A3B8] mt-1">{it.organizer}{it.event_type ? ` · ${it.event_type}` : ""}{it.city || it.location ? ` · ${it.city || it.location}` : ""}{it.country ? `, ${it.country}` : ""}</p>
                  <p className="text-sm text-[#94A3B8] mt-0.5">
                    Contact: <a href={`mailto:${it.contact_email}`} className="text-[#00F0FF] hover:underline">{it.contact_email}</a>
                    {" · "}<a href={it.website} target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline">Website</a>
                  </p>
                  {it.description && <p className="text-sm text-[#cbd5e1] mt-2 line-clamp-2">{it.description}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {it.status !== "approved" && (
                    <button onClick={() => approveEvent(it.id)} data-testid={`admin-approve-${it.id}`}
                      className="flex items-center gap-1 text-xs text-[#10B981] border border-[#10B981]/30 px-2.5 py-1.5 rounded hover:bg-[#10B981]/10">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                    </button>
                  )}
                  <button onClick={() => del(it.id)} data-testid={`admin-delete-${it.id}`}
                    className="text-[#EF4444] hover:bg-[#EF4444]/10 p-2 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}

          {!["messages", "jobreqs", "toolreqs", "eventreqs"].includes(tab) && displayItems.map((it) => (
            <div key={it.id} className="card-surface rounded-lg p-4 flex items-center justify-between gap-4" data-testid={`admin-item-${it.id}`}>
              <div className="min-w-0">
                <div className="font-display font-bold truncate">{it.name || it.title || it.email}</div>
                <p className="text-sm text-[#94A3B8] truncate">
                  {it.tagline || it.company_name || it.location || it.category || it.role || ""}
                  {it.status ? ` · ${it.status}` : ""}
                </p>
                {tab === "users" && it.verified && (
                  <p className="text-xs text-[#00F0FF] mt-0.5 flex items-center gap-1" data-testid={`admin-verify-meta-${it.id}`}>
                    <BadgeCheck className="w-3.5 h-3.5" />
                    Verified{it.verified_source ? ` · ${it.verified_source === "paid" ? "paid" : "admin-granted"}` : ""}
                    {it.verified_at ? ` · ${new Date(it.verified_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
                  </p>
                )}
                {tab === "events" && (
                  <p className="text-xs text-[#64748B] mt-0.5 font-mono-tech flex flex-wrap items-center gap-1.5" data-testid={`admin-event-meta-${it.id}`}>
                    <span className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10">{it.source || "manual"}</span>
                    <span className={it.auto_discovered ? "text-[#4da3ff]" : "text-[#4ade80]"}>{it.auto_discovered ? "auto-discovered" : "manual/anchor"}</span>
                    {it.reach_level ? <span>· {it.reach_level}</span> : null}
                    {it.last_verified ? <span>· verified {new Date(it.last_verified).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span> : null}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {tab === "users" && it.role === "company" && (
                  <button onClick={() => toggleVerify(it)} data-testid={`admin-verify-${it.id}`}
                    className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border transition ${it.verified ? "text-[#00F0FF] border-[#00F0FF]/40 bg-[#00F0FF]/10" : "text-[#94A3B8] border-white/15 hover:border-[#00F0FF]/40"}`}>
                    <BadgeCheck className="w-3.5 h-3.5" /> {it.verified ? "Verified" : "Verify"}
                  </button>
                )}
                {it.status === "pending_payment" && tab !== "users" && (
                  <button onClick={() => activate(it.id)} data-testid={`admin-activate-${it.id}`}
                    className="flex items-center gap-1 text-xs text-[#10B981] border border-[#10B981]/30 px-2.5 py-1.5 rounded hover:bg-[#10B981]/10">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Activate
                  </button>
                )}
                <button onClick={() => del(it.id)} data-testid={`admin-delete-${it.id}`}
                  className="text-[#EF4444] hover:bg-[#EF4444]/10 p-2 rounded">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        </>)}
        {tab === "countries" && <CountryIntelAdmin />}
        {tab === "citations" && <CitationsManager />}
        {tab === "jobexposure" && <JobExposureAdmin />}
        {tab === "aihuman" && <AIHumanAnalytics />}
        {tab === "aiprogress" && <AIProgressAdmin />}
        {tab === "autojobs" && <AutoJobsAdmin onChange={loadOverview} />}
        {tab === "datahub" && <DataHubAdmin />}
        {tab === "sidehustles" && <SideHustlesAdmin />}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
          <div className="card-surface rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-bold text-xl">{modal === "gen" ? "Generate AI News" : modal === "tool" ? "Add Tool" : modal === "free" ? "Add Free Listing (Admin Override)" : "Add News"}</h3>
              <button onClick={() => setModal(null)} data-testid="admin-modal-close"><X className="w-5 h-5 text-[#94A3B8]" /></button>
            </div>
            {modal === "gen" ? (
              <form onSubmit={generateNews} className="space-y-3">
                <p className="text-sm text-[#94A3B8]">Enter a topic and AI will write the article.</p>
                <input name="topic" required placeholder="e.g. New open-source LLM beats GPT-4 on benchmarks" className={field} data-testid="gen-topic" />
                <select name="category" className={field} defaultValue="Research">
                  {["Research", "Industry", "Funding", "Product", "Policy"].map((c) => <option key={c}>{c}</option>)}
                </select>
                <button disabled={busy} className="w-full bg-[#00F0FF] text-[#04121a] font-semibold py-2.5 rounded-md" data-testid="gen-submit">
                  {busy ? "Generating…" : "Generate"}
                </button>
              </form>
            ) : modal === "tool" ? (
              <form onSubmit={addTool} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input name="name" required placeholder="Tool name" className={field} data-testid="admin-tool-name" />
                  <input name="company" required placeholder="Company" className={field} data-testid="admin-tool-company" />
                </div>
                <select name="category" className={field} defaultValue="Chat & Assistants" data-testid="admin-tool-category">
                  {["Chat & Assistants", "Coding & Development", "Image Generation", "Video Generation", "Audio & Voice", "Writing & Content", "Productivity & Automation", "Analytics & Research", "AI Infrastructure", "AI Agents", "AI Hardware", "E-Commerce"].map((c) => <option key={c}>{c}</option>)}
                </select>
                <input name="pricing_model" required placeholder="Pricing (e.g. Free / Pro $20/mo)" className={field} data-testid="admin-tool-pricing" />
                <textarea name="description" required placeholder="Description" rows={3} className={field} data-testid="admin-tool-description" />
                <input name="website" required placeholder="Website (e.g. example.com)" className={field} data-testid="admin-tool-website" />
                <input name="tags" placeholder="Tags, comma separated (e.g. Chat, Coding)" className={field} data-testid="admin-tool-tags" />
                <div className="grid grid-cols-2 gap-3">
                  <input name="rating" type="number" step="0.1" min="0" max="5" placeholder="Rating (0-5)" className={field} data-testid="admin-tool-rating" />
                  <input name="users" placeholder="Users (e.g. 1M+)" className={field} data-testid="admin-tool-users" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input name="badge" placeholder="Badge (optional)" className={field} data-testid="admin-tool-badge" />
                  <select name="availability" className={field} defaultValue="live" data-testid="admin-tool-availability">
                    <option value="live">Live</option>
                    <option value="coming_soon">Coming Soon</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-[#94A3B8]"><input type="checkbox" name="featured" data-testid="admin-tool-featured" /> Featured (top of category)</label>
                <button disabled={busy} className="w-full bg-[#00F0FF] text-[#04121a] font-semibold py-2.5 rounded-md" data-testid="admin-tool-submit">
                  {busy ? "Saving…" : "Add tool"}
                </button>
              </form>
            ) : modal === "free" ? (
              <form onSubmit={addFreeListing} className="space-y-3" data-testid="admin-free-listing-form">
                <p className="text-sm text-[#94A3B8]">Publishes directly to the live jobs board — no payment required. Use only for listings you add yourself.</p>
                <input name="title" required placeholder="Job Title" className={field} data-testid="free-title" />
                <div className="grid grid-cols-2 gap-3">
                  <input name="company" required placeholder="Company Name" className={field} data-testid="free-company" />
                  <input name="company_website" placeholder="Company Website" className={field} data-testid="free-website" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input name="location" placeholder="Location" className={field} data-testid="free-location" />
                  <input name="country" placeholder="Country" className={field} data-testid="free-country" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <input name="salary_from" placeholder="Salary From" className={field} data-testid="free-salary-from" />
                  <input name="salary_to" placeholder="Salary To" className={field} data-testid="free-salary-to" />
                  <select name="currency" className={field} defaultValue="USD" data-testid="free-currency">
                    {["USD", "EUR", "GBP", "INR", "CAD", "AUD"].map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <select name="job_type" className={field} defaultValue="Full-time" data-testid="free-jobtype">
                    {["Full-time", "Part-time", "Contract", "Internship", "Freelance"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <label className="flex items-center gap-2 text-sm text-[#94A3B8]"><input type="checkbox" name="is_remote" data-testid="free-remote" /> Remote</label>
                </div>
                <textarea name="description" required placeholder="Job Description" rows={3} className={field} data-testid="free-description" />
                <input name="skills" placeholder="Skills, comma separated" className={field} data-testid="free-skills" />
                <input name="apply_url" required placeholder="Application URL (https://)" className={field} data-testid="free-apply-url" />
                <input name="contact_email" type="email" required placeholder="Contact Email" className={field} data-testid="free-contact-email" />
                <button disabled={busy} className="w-full bg-[#22C55E] text-[#04121a] font-semibold py-2.5 rounded-md" data-testid="free-submit">
                  {busy ? "Publishing…" : "Publish Free Listing"}
                </button>
              </form>
            ) : (
              <form onSubmit={addNews} className="space-y-3">
                <input name="title" required placeholder="Title" className={field} data-testid="news-title" />
                <textarea name="content" required placeholder="Content" rows={5} className={field} data-testid="news-content" />
                <input name="category" placeholder="Category" defaultValue="General" className={field} />
                <input name="image_url" placeholder="Image URL (optional)" className={field} />
                <button disabled={busy} className="w-full bg-[#00F0FF] text-[#04121a] font-semibold py-2.5 rounded-md" data-testid="news-submit">
                  {busy ? "Saving…" : "Publish"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
